import { isProdMode } from '@utils/env.ts'

import type { TransformedShelf } from '@state/shelf/types'

export function noop() { /* empty */ }

// this function receives a list of T and a callback that takes a T, the function returns the minimum of the result of the callback
export function getMin<T>(list: T[], callback: (item: T) => number): number {
  return list.reduce((min, item) => Math.min(min, callback(item)), Infinity)
}

// this function receives a list of T and a callback that takes a T, the function returns the maximum of the result of the callback
export function getMax<T>(list: T[], callback: (item: T) => number): number {
  return list.reduce((max, item) => Math.max(max, callback(item)), -Infinity)
}

export function getSum<T>(list: T[], callback: (item: T) => number): number {
  return list.reduce((sum, item) => sum + callback(item), 0)
}

export function isAnyValueSet(input: Record<any, unknown>): boolean {
  return Object.values(input)
    .filter(entry => entry !== undefined)
    .length > 0
}

export function dedupe<T>(list: T[]): T[] {
  return Array.from(new Set(list))
}

export function createArrayWithLength<T>(length: number, callback: (index: number) => T): T[] {
  return Array.from({ length }, (_, index) => callback(index))
}

function cleanObject<T extends Record<string, any>>(obj: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(obj)
      .filter(([, value]) => value !== undefined)
      .filter(([, value]) => value !== null)
      .filter(([, value]) => {
        if (typeof value === 'string') {
          return value.trim().length > 0
        }
        return true
      })
  ) as Partial<T>
}

export class MyUrlSearchParams extends URLSearchParams {
  constructor(init?: Record<string, any>) {
    super(init ? cleanObject(init) : undefined)
  }

  append(key: string, value?: string | number | boolean) {
    if (!value) return
    super.append(key, value.toString())
  }

  toString(): string {
    const str = super.toString()
    return str ? `?${str}` : ''
  }
}

type Normalizer = (value: number) => number

export function createNormalizer(smallest: number, biggest: number): Normalizer {
  const diff = biggest - smallest
  return (value: number) => (value - smallest) / diff
}

const VALID_GTIN_LENGTHS = [8, 12, 13, 14]

export function isValidGTIN(gtin: string) {
  // support GTIN-8, GTIN-12, GTIN-13, and GTIN-14
  if (!VALID_GTIN_LENGTHS.includes(gtin.length)) return false

  // must contain only digits
  return /^\d+$/.test(gtin)
}

export function getPercentile(numbers: number[], percentile: number): number {
  if (numbers.length === 0) {
    throw new Error('Cannot calculate percentile of empty array')
  }

  if (percentile < 0 || percentile > 100) {
    throw new Error('Percentile must be between 0 and 100')
  }

  const sorted = [...numbers].sort((a, b) => a - b)

  // Calculate the index for the percentile
  const index = (percentile / 100) * (sorted.length - 1)
  const lower = Math.floor(index)
  const upper = Math.ceil(index)

  // Ensure indices are within bounds
  if (lower === upper) {
    return sorted[lower]!
  } else {
    return (sorted[lower]! + sorted[upper]!) / 2
  }
}

export function getMedian(numbers: number[]): number {
  return getPercentile(numbers, 50)
}

export function isSameDay(dateA: Date, dateB: Date) {
  return dateA.getFullYear() === dateB.getFullYear() &&
        dateA.getMonth() === dateB.getMonth() &&
        dateA.getDate() === dateB.getDate()
}

export function getValueOfCssVariable(cssProp: string) {
  const value = window.getComputedStyle(document.documentElement).getPropertyValue(cssProp)

  if (value === '' && !isProdMode) {
    throw new Error(`CSS property ${cssProp} is not defined on the :root`)
  }

  return value
}

export function createLazy<T>(initializer: () => T): () => T {
  let cached: T | undefined = undefined

  return () => {
    cached ??= initializer()
    return cached
  }
}

export function pipe<T>(input: T, ...fns: ((arg: T) => T)[]): T {
  return fns.reduce((acc, fn) => fn(acc), input)
}

export function getFacingCount(shelf: TransformedShelf) {
  const hasStockCount = shelf.statistics?.hasStockCount ?? 0
  const noStockCount = shelf.statistics?.noStockCount ?? 0
  const unknownStockCount = shelf.statistics?.unknownStockCount ?? 0

  return hasStockCount + noStockCount + unknownStockCount
}